import axios from 'axios';

import { Fragment, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { CheckRounded, IosShareRounded, SearchRounded, TuneOutlined } from '@mui/icons-material';

// Redux.
import { connect } from 'react-redux';
import { signoutUser } from '../redux/actions/userActions';

// Router.
import { useNavigate } from 'react-router-dom';

// Firebase.
import firebase from 'firebase/compat/app';
import { getAnalytics, logEvent } from "firebase/analytics";
import 'firebase/compat/auth';

// Components.
import BottomMenu from '../components/BottomMenu';
import FullWidthButton from '../components/FullWidthButton';
import SigninRequired from '../components/SigninRequired';
import StatusToast from '../components/StatusToast';
import ThemePicker from '../components/ThemePicker';
import { shareContent, SHARE_FAILURE_MESSAGE } from '../utils/shareContent';


import { clearAllTipsOnLogout } from '../constants/local_storage_constants';
import { PREFERENCE_COPY_TITLE } from '../constants/labels';
import { validTokenExists } from '../firebaseHelper';
import DownloadAppLinks from '../components/DownloadAppLinks';
import {
    playbackSkipSettingsAccessibilityValue,
    playbackSkipSettingsSummary,
    readPlaybackSkipSettings,
} from '../utils/playbackSettings';



const Menu = ({
    authenticated,
    phoneNumber,
    signoutUser,
}) => {
    const navigate = useNavigate();
    const textareaRef = useRef(null);
    const playbackSettings = readPlaybackSkipSettings();
    const [copied, setCopied] = useState(false);
    const [shareError, setShareError] = useState('');

    const shareApp = async () => {
        setCopied(false);
        setShareError('');
        document.getElementById('audio')?.pause();
        logEvent(getAnalytics(), 'share_app');
        try {
            const result = await shareContent({
                text: 'Hear from humans with Podeux',
                url: 'https://www.podeux.com',
            });
            if (result === 'copied') setCopied(true);
        } catch (_) {
            setShareError(SHARE_FAILURE_MESSAGE);
        }
    };

    const tableOfContentsSections = [
        {
            title: 'Home',
            links: [
                { label: 'Chat history', path: '/chat_threads' },
            ],
        },
        {
            title: "Library",
            links: [
                { label: 'Saved', path: '/saved' },
                { label: 'Played', path: '/played' },
                { label: 'Notes', path: '/notes' },
            ],
        },
        {
            title: 'Other',
            links: [
                {
                    label: 'Playback',
                    path: '/settings/playback',
                    trailingText: playbackSkipSettingsSummary(playbackSettings),
                    accessibilityLabel:
                        `Playback, ${playbackSkipSettingsAccessibilityValue(playbackSettings)}`,
                },
                { label: 'Support', path: '/support' },
            ],
        },
    ];

    useEffect(() => {
        const analytics = getAnalytics();
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'menu'
        });
    }, []);

    useEffect(() => {
        const unregisterAuthObserver = firebase.auth().onAuthStateChanged(user => {
            if (!!user) {
                // Expected to be signed in.
            } else {
                signoutUser();
                const analytics = getAnalytics();
                logEvent(analytics, 'logout');
            }
        });
        return () => unregisterAuthObserver(); // Make sure we un-register Firebase observers when the component unmounts.
    }, [signoutUser]);

    const signoutClick = () => {
        clearAllTipsOnLogout();

        firebase.auth().signOut().then(() => {

        }).catch((error) => {
            // An error happened.
        });
    }

    const [feedback, setFeedback] = useState('');
    const [feedbackSuccess, setFeedbackSuccess] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (feedback.trim()) {
            validTokenExists()
                .then(() => {
                    axios
                        .post('/feedback', {
                            text: feedback,
                        })
                        .then((res) => {
                            if (res.data.error) {
                                window.alert('Error: ' + res.data.error);
                            } else {
                                setFeedback('');
                                setFeedbackSuccess(true);
                            }
                        })
                        .catch((err) => {
                            window.alert('Error: ' + err.response.data.error);
                        });
                })
                .catch((e) => {
                    // Not logged in.
                });
        }
    };

    const navigateTo = (path) => () => {
        navigate(path);
    };

    const resetFeedbackState = () => {
        setFeedbackSuccess(false);
        requestAnimationFrame(() => {
            textareaRef.current?.focus();
        });
    };

    const displayPhoneNumber = phoneNumber || 'Signed in';


    if (authenticated !== true) {
        return <SigninRequired authenticated={authenticated} />;
    } else {
        return (
            <div className="container hasBottomMenu menuPageContainer">
                <StatusToast open={copied} message='Copied to clipboard' onClose={() => setCopied(false)} />
                <StatusToast tone='error' open={Boolean(shareError)} message={shareError} onClose={() => setShareError('')} />
                <BottomMenu />
                <div className='menuPage'>
                    <div className='menuPageContent'>
                        <header className='menuAccountIdentity' aria-label='Signed-in account'>
                            <div className='menuPhoneNumber'>
                                {displayPhoneNumber}
                            </div>
                        </header>

                        <section className='menuListCard' aria-label='Discovery'>
                            <FullWidthButton
                                Icon={SearchRounded}
                                iconStyle={{ fontSize: '22px' }}
                                title='Search'
                                onClick={navigateTo('/search')}
                                containerPadding='0px'
                                contentGap='12px'
                                buttonClassName='menuListButton'
                                textClassName=''
                            />
                            <FullWidthButton
                                Icon={TuneOutlined}
                                title={PREFERENCE_COPY_TITLE}
                                onClick={navigateTo('/settings')}
                                containerPadding='0px'
                                contentGap='12px'
                                buttonClassName='menuListButton'
                                textClassName=''
                            />
                        </section>

                        <section className='menuSectionCard'>
                            {feedbackSuccess ?
                                <div className='menuFeedbackSuccess'>
                                    <div className='menuFeedbackSuccessBadge' aria-hidden='true'>
                                        <CheckRounded style={{ fontSize: '24px' }} />
                                    </div>

                                    <div>
                                        <div className='menuFeedbackSuccessTitle'>
                                            Thanks for the note.
                                        </div>
                                        <div className='menuFeedbackDescription'>
                                            Your feedback is in. If anything else feels off, send another one.
                                        </div>
                                    </div>

                                    <button type='button' className='menuGhostButton' onClick={resetFeedbackState}>
                                        Send more feedback
                                    </button>
                                </div>
                                :
                                <div>
                                    <form onSubmit={handleSubmit} className='menuFeedbackForm'>
                                        <textarea
                                            ref={textareaRef}
                                            className='menuFeedbackTextArea'
                                            value={feedback}
                                            onChange={(e) => setFeedback(e.target.value)}
                                            placeholder='What should we remove or build next?'
                                            required
                                        />
                                        <FullWidthButton
                                            title='Send feedback'
                                            type='submit'
                                            showChevron={false}
                                            containerPadding='0px'
                                            buttonClassName='menuSubmitButton'
                                            textClassName='menuSubmitButtonText'
                                        />
                                    </form>
                                </div>
                            }
                        </section>

                        <div className='menuGroups'>
                            {tableOfContentsSections.map((section) => (
                                <section key={section.title} className='menuSection'>
                                    <div className='menuSectionLabel'>
                                        {section.title}
                                    </div>
                                    <div className='menuListCard'>
                                        {section.links.map((link) => (
                                            <Fragment key={link.path}>
                                                <FullWidthButton
                                                    title={link.label}
                                                    Icon={link.icon}
                                                    trailingText={link.trailingText}
                                                    accessibilityLabel={link.accessibilityLabel}
                                                    onClick={navigateTo(link.path)}
                                                    containerPadding='0px'
                                                    buttonClassName='menuListButton'
                                                    textClassName=''
                                                />
                                                {link.path === '/settings/playback' && <ThemePicker />}
                                            </Fragment>
                                        ))}

                                        {section.title === 'Other' && <FullWidthButton
                                            title='Share Podeux'
                                            Icon={IosShareRounded}
                                            onClick={shareApp}
                                            showChevron={false}
                                            centerContent={false}
                                            containerPadding='0px'
                                            buttonClassName='menuListButton'
                                            textClassName=''
                                        />}
                                        {section.title === 'Other' && (
                                            <button
                                                type='button'
                                                onClick={signoutClick}
                                                className='menuListButton menuListActionButton menuListActionButtonDanger'
                                            >
                                                <span className='fullWidthButtonText'>
                                                    Sign out
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                </section>
                            ))}
                        </div>

                        <section className='menuSectionCard menuDownloadSection'>
                            <div className='menuDownloadTitle'>
                                For your phone
                            </div>
                            <div className='menuDownloadDescription'>
                                Try the app for the smoothest
                                <br />
                                listening experience
                            </div>

                            <DownloadAppLinks compact />
                        </section>

                    </div>
                </div>
            </div>
        );
    }
}

Menu.propTypes = {
    authenticated: PropTypes.bool
}

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    phoneNumber: state.user.phoneNumber,
})

const mapActionsToProps = {
    signoutUser,
}

export default connect(mapStateToProps, mapActionsToProps)(Menu);
